import { apiFailureResponseSchema, responseSchemas } from "@word-duel/shared";
import type { GameView, MoveView, Seat } from "@word-duel/shared";
import { z, type ZodType } from "zod";

const configuredApiOrigin = import.meta.env.VITE_API_BASE_URL?.trim().replace(/\/+$/u, "") ?? "";
const API_ROOT = `${configuredApiOrigin}/api/v1`;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly revision?: number;

  constructor(status: number, code: string, message: string, details?: unknown, revision?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    if (revision !== undefined) this.revision = revision;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "DELETE";
  token?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(
  path: string,
  responseSchema: ZodType<T>,
  options: RequestOptions = {}
): Promise<T> {
  const headers = new Headers({ Accept: "application/json" });
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.token) headers.set("Authorization", `Bearer ${options.token}`);

  let response: Response;
  try {
    const init: RequestInit = {
      method: options.method ?? "GET",
      headers,
      cache: "no-store",
      credentials: "omit"
    };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;
    response = await fetch(`${API_ROOT}${path}`, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      "Could not reach the game server. Check your connection and try again."
    );
  }

  const payload: unknown = await response.json().catch(() => null);
  if (response.ok) {
    const success = z
      .object({ ok: z.literal(true), data: responseSchema })
      .strict()
      .safeParse(payload);
    if (success.success) return success.data.data;
    throw new ApiError(
      502,
      "INVALID_RESPONSE",
      "The game server returned an invalid response. Refresh and try again."
    );
  }
  const failure = apiFailureResponseSchema.safeParse(payload);
  if (!failure.success) {
    throw new ApiError(
      response.status,
      "INVALID_RESPONSE",
      "The game server returned an invalid error response."
    );
  }
  throw new ApiError(
    response.status,
    failure.data.error.code,
    failure.data.error.message,
    failure.data.error.details,
    failure.data.error.revision
  );
}

export interface SessionResponse {
  game: GameView;
  token: string;
  seat: Seat;
}

export interface ActionResponse {
  game: GameView;
  move: MoveView;
}

export interface SelfieRecord {
  mimeType: "image/jpeg" | "image/png" | "image/webp";
  base64: string;
  width: number;
  height: number;
  byteCount?: number;
  sha256?: string;
  updatedAt?: string;
}

export interface NotificationConfig {
  publicKey: string;
  enabled: boolean;
}

export interface NotificationStatus {
  enabled: boolean;
}

export const api = {
  createGame: (nickname: string, timerSeconds: number | null) =>
    request<SessionResponse>("/games", responseSchemas.credentials, {
      method: "POST",
      body: { nickname, timerSeconds }
    }),
  joinGame: (code: string, nickname: string) =>
    request<SessionResponse>(
      `/games/${encodeURIComponent(code)}/join`,
      responseSchemas.credentials,
      {
        method: "POST",
        body: { nickname }
      }
    ),
  getGame: (code: string, token: string, signal?: AbortSignal) =>
    request<{ game: GameView }>(`/games/${encodeURIComponent(code)}`, responseSchemas.game, {
      token,
      ...(signal ? { signal } : {})
    }).then((result) => result.game),
  getMoves: (code: string, token: string, signal?: AbortSignal) =>
    request<{ moves: MoveView[] }>(
      `/games/${encodeURIComponent(code)}/moves`,
      responseSchemas.moves,
      {
        token,
        ...(signal ? { signal } : {})
      }
    ).then((result) => result.moves),
  getNotificationConfig: (code: string, token: string) =>
    request<NotificationConfig>(
      `/games/${encodeURIComponent(code)}/notifications`,
      responseSchemas.notificationConfig,
      { token }
    ),
  registerNotification: (code: string, token: string, subscription: unknown) =>
    request<NotificationStatus>(
      `/games/${encodeURIComponent(code)}/notifications`,
      responseSchemas.notificationStatus,
      { method: "POST", token, body: subscription }
    ),
  deleteNotification: (code: string, token: string, endpoint: string) =>
    request<NotificationStatus>(
      `/games/${encodeURIComponent(code)}/notifications`,
      responseSchemas.notificationStatus,
      { method: "DELETE", token, body: { endpoint } }
    ),
  place: (code: string, token: string, body: unknown) =>
    request<ActionResponse>(`/games/${encodeURIComponent(code)}/place`, responseSchemas.action, {
      method: "POST",
      token,
      body
    }),
  pass: (code: string, token: string, body: unknown) =>
    request<ActionResponse>(`/games/${encodeURIComponent(code)}/pass`, responseSchemas.action, {
      method: "POST",
      token,
      body
    }),
  exchange: (code: string, token: string, body: unknown) =>
    request<ActionResponse>(`/games/${encodeURIComponent(code)}/exchange`, responseSchemas.action, {
      method: "POST",
      token,
      body
    }),
  resign: (code: string, token: string, body: unknown) =>
    request<ActionResponse>(`/games/${encodeURIComponent(code)}/resign`, responseSchemas.action, {
      method: "POST",
      token,
      body
    }),
  rematch: (code: string, token: string) =>
    request<SessionResponse>(
      `/games/${encodeURIComponent(code)}/rematch`,
      responseSchemas.credentials,
      {
        method: "POST",
        token,
        body: { idempotencyKey: crypto.randomUUID() }
      }
    ),
  getSelfie: (code: string, token: string, signal?: AbortSignal) =>
    request<{ selfie: SelfieRecord }>(
      `/games/${encodeURIComponent(code)}/selfie`,
      responseSchemas.selfie,
      {
        token,
        ...(signal ? { signal } : {})
      }
    ).then((result) => result.selfie),
  saveSelfie: (code: string, token: string, selfie: SelfieRecord) =>
    request<{ selfie: SelfieRecord }>(
      `/games/${encodeURIComponent(code)}/selfie`,
      responseSchemas.selfie,
      {
        method: "POST",
        token,
        body: selfie
      }
    ).then((result) => result.selfie),
  deleteSelfie: (code: string, token: string) =>
    request<{ deleted: boolean }>(
      `/games/${encodeURIComponent(code)}/selfie`,
      responseSchemas.deleted,
      {
        method: "DELETE",
        token
      }
    )
};
